import type { ServerDetail } from '@/types/fleet';
import type { Playbook } from '@/types/provisioning';

/**
 * The per-server action menu (curated shortcuts) is backed by the generic
 * Provisioning API. Each curated action maps to one playbook template by its
 * canonical `name` and supplies the survey args targeting that server.
 *
 * This is the source of truth tying `action-menu.tsx` to `templates.yml`.
 */
export interface ServerActionDef {
  /** Stable key — also the action-menu button key. */
  key: string;
  label: string;
  /** Playbook `name` in the provisioning catalogue. */
  playbookName: string;
  /**
   * Whether the action mutates server state. Mutating actions get a pending
   * overlay on the status badge; pure-read actions do not (per the spec).
   */
  stateChanging: boolean;
  /** Confirmation-dialog copy — plain language, no protocol guarantees. */
  confirmCopy: (server: ServerDetail) => string;
  /** Optional visibility predicate (e.g. AWG rotation needs amneziawg). */
  visible?: (server: ServerDetail) => boolean;
  /**
   * Builds the survey args for the run. Most playbooks accept the server name
   * via `target_server`. Every key MUST exist in the target template's survey:
   * the runner REJECTS unknowns with a 400 (`provisioning.service.ts`), it does
   * not drop them. `pruneSurveyArgs` prunes only for the manual survey form;
   * the curated path posts these verbatim.
   */
  buildArgs: (server: ServerDetail) => Record<string, unknown>;
}

/** Args keyed on the server name — covers the survey field names in use. */
function targetArgs(server: ServerDetail): Record<string, unknown> {
  return { target_server: server.name, target: server.name };
}

/**
 * The six non-destructive per-server actions wired in PR-E #2. DESTRUCTIVE
 * actions (decommission / quarantine / recover) are intentionally absent —
 * they ship in PR-E #3.
 */
export const SERVER_ACTIONS: ServerActionDef[] = [
  {
    key: 'health-check',
    label: 'Health Check',
    playbookName: 'Health Check',
    stateChanging: false,
    confirmCopy: (s) => `Run comprehensive health checks on ${s.name}. This is read-only.`,
    buildArgs: targetArgs,
  },
  {
    key: 'debug-tunnel',
    label: 'Debug Tunnel',
    playbookName: 'Debug VPN Tunnel',
    stateChanging: false,
    confirmCopy: (s) =>
      `Diagnose tunnel connectivity on ${s.name}. This inspects the running containers; it changes nothing.`,
    buildArgs: targetArgs,
  },
  {
    key: 'check-russia',
    label: 'Check Russia Reachability',
    playbookName: 'Check Russia Reachability',
    stateChanging: false,
    confirmCopy: (s) =>
      `Test whether ${s.name}'s public IP is reachable from Russia via check-host.net. This is read-only.`,
    buildArgs: targetArgs,
  },
  {
    key: 'update-images',
    label: 'Update Images',
    playbookName: 'Update Docker Images',
    stateChanging: true,
    confirmCopy: (s) =>
      `Pull the latest Docker images on ${s.name} and restart its containers. Connected sessions may briefly disconnect and reconnect.`,
    buildArgs: targetArgs,
  },
  {
    // Re-runs the full proxy_ingress role (nginx/3proxy + the vpn-proxy auth
    // sidecar). Needed to roll role changes onto an already-ACTIVE box —
    // "Update Images" can't bootstrap a container/unit that isn't there yet.
    // Proxy-ingress only.
    key: 'reprovision-proxy-ingress',
    label: 'Re-provision',
    playbookName: 'Provision Proxy Ingress',
    stateChanging: true,
    visible: (s) => s.role === 'proxy_ingress',
    confirmCopy: (s) =>
      `Re-run the full proxy-ingress provision on ${s.name} (nginx + 3proxy + the vpn-proxy auth sidecar). 3proxy restarts, so CONNECT sessions briefly drop and reconnect. Egress is unchanged (it re-uses this server's stored config).`,
    // Only the fields the "Provision Proxy Ingress" survey defines (the runner
    // rejects unknowns). Exit-node vars are intentionally OMITTED — same as the
    // backend's dispatchProvision — so the role keeps this server's existing
    // egress (direct, for proxy-es) instead of forcing a tunnel.
    buildArgs: (s) => ({
      server_name: s.name,
      server_ip: s.public_ip,
      proxy_hostname: s.dns_hostname ?? s.public_ip,
      proxy_region: s.region.country,
    }),
  },
  {
    // Re-runs the bootstrap_edge role (Xray VLESS-Reality on :443 forwarding to
    // the API origin). Use to roll role/seed changes onto an ACTIVE edge or to
    // rotate it. bootstrap_origin defaults in the playbook survey. server_name is
    // REQUIRED by the Provision Bootstrap Edge survey (and the post-deploy fleet
    // reality-config callback keys on it). Bootstrap-edge only.
    key: 'reprovision-bootstrap-edge',
    label: 'Re-provision',
    playbookName: 'Provision Bootstrap Edge',
    stateChanging: true,
    visible: (s) => s.role === 'bootstrap_edge',
    confirmCopy: (s) =>
      `Re-run the bootstrap-edge provision on ${s.name} (Xray VLESS-Reality on :443 → API origin). Xray restarts briefly; the edge keeps its persisted Reality keypair/UUID so existing clients stay valid.`,
    buildArgs: (s) => ({
      server_name: s.name,
      server_ip: s.public_ip,
    }),
  },
  {
    // Fast, config-only refresh of an ACTIVE bootstrap edge: re-renders the Xray
    // config from the current template + restarts + self-tests end-to-end,
    // WITHOUT re-keying (persisted keypair/UUID reused → published seed stays
    // valid). Use to push a template/config fix (e.g. a domainStrategy change)
    // to a live edge; use "Re-provision" for a fresh box or a deliberate re-key.
    // Passes server_name so the post-deploy fleet reality-config callback hits
    // the right record. Bootstrap-edge only.
    key: 'reconfigure-bootstrap-edge',
    label: 'Reconfigure',
    playbookName: 'Reconfigure Bootstrap Edge',
    stateChanging: true,
    visible: (s) => s.role === 'bootstrap_edge',
    confirmCopy: (s) =>
      `Reconfigure ${s.name} — re-render the Xray config from the current template, restart, and self-test the full Reality path end-to-end. No re-key: the edge keeps its persisted Reality keypair/UUID, so the published seed and pinned clients stay valid.`,
    buildArgs: (s) => ({
      server_ip: s.public_ip,
      server_name: s.name,
    }),
  },
  {
    // Re-runs the v2ray_edge role (VLESS+Reality on :443) — the stealth-VPN edge the
    // app connects to in IR/RU/CN. Use to roll role/version changes onto a live edge
    // (e.g. the Xray upgrade). transport defaults to reality; server_name is required
    // by the Deploy V2Ray Edge survey. V2ray-edge only.
    key: 'reprovision-v2ray-edge',
    label: 'Re-provision',
    playbookName: 'Deploy V2Ray Edge',
    stateChanging: true,
    visible: (s) => s.role === 'v2ray_edge',
    confirmCopy: (s) =>
      `Re-run the v2ray-edge deploy on ${s.name} (VLESS+Reality on :443). Xray restarts briefly; the edge keeps its persisted Reality keypair/UUID, so existing stealth clients stay valid.`,
    buildArgs: (s) => ({
      server_name: s.name,
      server_ip: s.public_ip,
      transport: 'reality',
    }),
  },
  {
    // Re-runs the full server provision (vpn_deploy role) on an EXISTING node,
    // re-applying its current role + protocol set. For vpn_server / exit_node /
    // gateway. server_name + server_ip + role are required by the Provision Server
    // survey; protocol carries the node's current protocol set so it re-applies the
    // same config rather than defaults.
    key: 'reprovision-server',
    label: 'Re-provision',
    playbookName: 'Provision Server',
    stateChanging: true,
    visible: (s) => ['vpn_server', 'exit_node', 'gateway'].includes(s.role),
    confirmCopy: (s) =>
      `Re-run the full provision on ${s.name} (role: ${s.role}), re-applying its protocol set [${s.protocols.map((p) => p.protocol).join(', ') || 'none'}]. Containers reconcile in place rather than being torn down first, so connected sessions should survive.`,
    buildArgs: (s) => ({
      server_name: s.name,
      server_ip: s.public_ip,
      role: s.role,
      protocol: s.protocols.map((p) => p.protocol).join(','),
      // MUST be 'true' for this button. The play's default (false) runs
      // `docker compose down --remove-orphans` + `docker network rm` BEFORE
      // re-creating (provision-server.yml) — correct for a fresh or wedged box,
      // a full outage on a live one. Re-provision only ever targets an existing
      // node, so preserving the stack is the only safe default here.
      preserve_running_stack: 'true',
    }),
  },
  {
    key: 'restart',
    label: 'Restart',
    playbookName: 'VPN Control',
    stateChanging: true,
    confirmCopy: (s) =>
      `Restart all VPN containers on ${s.name}. Connected sessions may briefly disconnect and reconnect.`,
    buildArgs: (s) => ({ ...targetArgs(s), vpn_action: 'restart' }),
  },
  {
    key: 'rotate-awg-obfuscation',
    label: 'Rotate AWG Obfuscation',
    playbookName: 'Rotate AWG Obfuscation',
    stateChanging: true,
    confirmCopy: (s) =>
      `Rotate AmneziaWG obfuscation parameters on ${s.name} for DPI resistance. AmneziaWG clients re-handshake automatically.`,
    visible: (s) => s.protocols.some((p) => p.protocol === 'amneziawg'),
    buildArgs: targetArgs,
  },
];

/** Resolves a curated action to its catalogue playbook, if loaded. */
export function findActionPlaybook(
  action: ServerActionDef,
  playbooks: Playbook[] | undefined,
): Playbook | undefined {
  return playbooks?.find((p) => p.name === action.playbookName);
}
